import { Capacitor } from '@capacitor/core';
import { isIOSPlatform } from '../../utils/platform-detection';
import { initializeSubscriptionManager } from '../premium/subscription-manager.service';
import { auth } from '../firebase/core';
import logger from '../../utils/logger';

// Firebase Auth Bridge removed for production simplification

/**
 * Initialize native plugins and register listeners
 * Call this on app startup
 */
export const initializeNativePlugins = () => {
  // The Capacitor-2-era `Plugins` registry (and its enumerable plugin list)
  // was removed from @capacitor/core — log platform readiness instead.
  if (Capacitor.isNativePlatform()) {
    logger.info('Native plugins initialized for production');
  } else {
    logger.info('Running on web — native plugins unavailable');
  }
  
  // Log platform information
  logger.info('Running on iOS platform:', isIOSPlatform());
  
  // Initialize platform-specific features
  if (isIOSPlatform()) {
    initializeIOSFeatures();
  }
  
  // Initialize subscription manager when user is authenticated
  auth.onAuthStateChanged((user: any) => {
    if (user) {
      logger.info('User authenticated, initializing subscription manager');
      initializeSubscriptionManager(user.uid).catch(error => {
        logger.error('Failed to initialize subscription manager:', error);
      });
    }
  });
};

/**
 * Initialize iOS-specific features
 */
const initializeIOSFeatures = () => {
  logger.info('Initializing iOS-specific features');
  
  // Add a message handler for WebView network testing
  if (typeof window !== 'undefined') {
    // Test network connectivity
    testWebViewNetwork();
    
    // Add a message handler for the native bridge
    window.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'nativeBridge') {
        logger.info('Received message from native bridge:', event.data);
      }
    });
  }
};

/**
 * Test WebView network connectivity
 */
const testWebViewNetwork = () => {
  logger.info('Testing WebView network connectivity...');
  
  // Test Firebase authentication endpoints
  testEndpoint('https://identitytoolkit.googleapis.com', 'Firebase Auth');
  testEndpoint('https://marune-da0a1.firebaseio.com/.json?shallow=true', 'Firebase Database');
  testEndpoint('https://firebasestorage.googleapis.com', 'Firebase Storage');
  
  // Test general internet connectivity
  testEndpoint('https://www.google.com', 'Google');
};

/**
 * Test if an endpoint is reachable
 */
const testEndpoint = (url: string, name: string) => {
  logger.info(`Testing connectivity to ${name} (${url})...`);
  
  fetch(url, { 
    method: 'HEAD',
    mode: 'no-cors', // Important for CORS issues
    cache: 'no-cache'
  })
    .then(() => {
      // Success (or CORS error, which still means network is working)
      logger.info(`✅ Connection to ${name} successful`);
    })
    .catch((error) => {
      // Real network error
      logger.error(`❌ Connection to ${name} failed:`, error);
    });
};

const exportedObject = {
  initializeNativePlugins
};

export default exportedObject;